import { Command } from 'commander'
import fs from 'fs-extra'
import path from 'path'
import prompts from 'prompts'
import z from 'zod'

import { preFlightInit } from '@preflights/preFlightInit'
import { txt } from '@utils/colors'
import { FrameworkName } from '@utils/frameworks'
import { handleError } from '@utils/handleError'
import { logger } from '@utils/logger'
import { scaffoldNewProject, setupProjectConfig } from '@utils/project'
import { promptForNewProject } from '@utils/prompts'
import { GLOBAL_CSS_V4, UTILS } from '@utils/templates'

export const PROJECT_DEPENDENCIES = [
  'tw-animate-css',
  'class-variance-authority',
  'clsx',
  'tailwind-merge',
  'lucide-react',
]
export const VITE_EXTRA_DEPENDENCIES = ['@tailwindcss/vite']

export const initOptionsSchema = z.object({
  cwd: z.string(),
  skipPrompts: z.boolean(),
  defaultConfigurations: z.boolean(),
})

export type InitOptions = z.infer<typeof initOptionsSchema>

export const init = new Command()
  .name('init')
  .description('initializes your project with required dependencies')
  .option('-s,--skipPrompts', 'skip confirmation prompts', false)
  .option('-d,--defaultConfigurations', 'use default configurations', false)
  .option('-c,--cwd <cwd>', 'current working directory', process.cwd())
  .action(async (opts) => {
    try {
      const options = initOptionsSchema.parse(opts)

      await executeInit(options)

      logger.break()
      logger.info(`${txt.success('Success!')} Project initialization completed. You may now add components.`)
      logger.break()
    } catch (error) {
      handleError(error)
    }
  })

export const createGlobalCssFile = async (projectDir: string, hasSrcDir: boolean, framework: FrameworkName) => {
  try {
    let cssPath: string
    switch (framework) {
      case 'vite':
        cssPath = path.join(projectDir, 'src', 'index.css')
        break

      case 'next-app':
        cssPath = hasSrcDir
          ? path.join(projectDir, 'src', 'app', 'globals.css')
          : path.join(projectDir, 'app', 'globals.css')
        break

      case 'next-pages':
        cssPath = hasSrcDir
          ? path.join(projectDir, 'src', 'styles', 'globals.css')
          : path.join(projectDir, 'styles', 'globals.css')
        break
      default:
        throw new Error(`Unsupported framework: ${framework}`)
    }

    await fs.ensureFile(cssPath)
    await fs.writeFile(cssPath, GLOBAL_CSS_V4, 'utf-8')
  } catch (error) {
    throw new Error(`Failed to create global CSS: ${error.message}`)
  }
}

export const createUtils = async (projectDir: string, hasSrcDir: boolean, framework: FrameworkName) => {
  try {
    const baseDir =
      framework === 'vite' ? path.join(projectDir, 'src') : hasSrcDir ? path.join(projectDir, 'src') : projectDir

    const utilsDir = path.join(baseDir, 'lib')
    await fs.ensureDir(utilsDir)
    await fs.writeFile(path.join(utilsDir, 'utils.ts'), UTILS, 'utf8')
  } catch (error) {
    throw new Error(`Failed to create utils.ts: ${error}`)
  }
}

export const executeInit = async (options: InitOptions) => {
  const { projectInfo } = await preFlightInit(options)

  const hasExistingProject = !!projectInfo

  if (!hasExistingProject) {
    // Create new project if there is no existing project
    const confirmation = await prompts({
      type: 'confirm',
      name: 'confirmNewProject',
      message: `No package.json found at ${txt.bold(txt.info(options.cwd))}. Create a new project?`,
      initial: false,
    })

    if (!confirmation.confirmNewProject) process.exit()

    const projectPrompts = await promptForNewProject(options.skipPrompts)

    const { projectPath } = await scaffoldNewProject(options, projectPrompts)

    await setupProjectConfig(projectPath, projectPrompts.framework, projectPrompts.useSrcDir)
  } else {
    // Setup necessary configuration files & install dependencies only
    // if there is already an existing project
    await setupProjectConfig(options.cwd, projectInfo.framework.name, projectInfo.hasSrcDir)
  }

  return projectInfo
}
